import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { updateOrders } from "../../redux/notificationsSlice";

import { loadOrdersWithUsers, parseAxiosError } from "../../utils/AxiosUtils";
import { loadUser } from "../../utils/AuthDataUtils";
import {
  datetimeStringToStringAgo,
  formatPhoneString,
  inlineReturn,
  optionsToValues,
  parseOrderStatus,
  statusOptions,
} from "../../utils/StringUtils";

import { Col, Input, Row } from "reactstrap";
import Select from "react-select";

import { FaRegCommentAlt, FaRegComments } from "react-icons/fa";

import CustomTable from "../../components/CustomTable";
import CustomPagination from "../../components/CustomPagination";
import CustomOrderDetails from "../../components/CustomOrderDetails";

import { RELOAD_TIME_MS } from "../../constants/Paramters";

export default function Orders() {
  const user = loadUser();
  console.log("Render Orders, user:", user !== null);

  const dispatch = useDispatch();

  const clearTimerRef = useRef();

  const [search, setSearch] = useState("");
  const [searchLock, setSearchLock] = useState("");
  const [statues, setStatuses] = useState([]);
  const [statuesLock, setStatuesLock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [orders, setOrders] = useState([]);
  const [id, setId] = useState();
  const [refreshedAt, setRefreshedAt] = useState();

  const error = (message) => {
    console.warn(message);
  };

  const handlePageLoaded = (res) => {
    const content = res.content;
    setOrders(content);
    setTotalPages(res.totalPages);
    setId((oldId) => (!oldId && content.length ? content[0].id : oldId));
    setRefreshedAt(new Date());
  };

  useEffect(() => {
    const reload = () => {
      console.log("Orders reload", page);
      let params = ["page=" + page];
      if (searchLock && searchLock.length) {
        params.push("key=" + searchLock);
      }
      if (statuesLock && statuesLock.length) {
        params.push("status=" + statuesLock.join(","));
      }
      loadOrdersWithUsers(params.join("&"))
        .then(handlePageLoaded)
        .catch((err) => error(parseAxiosError(err)))
        .finally(() => {
          clearTimeout(clearTimerRef.current);
          clearTimerRef.current = setTimeout(reload, RELOAD_TIME_MS);
        });
    };

    clearTimerRef.current = setTimeout(reload, RELOAD_TIME_MS);
    return () => clearTimeout(clearTimerRef.current);
  }, [page, searchLock, statuesLock]);

  const onSearchAndStatus = useCallback(() => {
    console.log("Orders onSearchAndStatus", search, statues);
    let params = ["page=0"];
    if (search && search.length) {
      params.push("key=" + search);
    }
    if (statues && statues.length) {
      params.push("status=" + statues.join(","));
    }
    setSearchLock(search);
    setStatuesLock(statues);
    setPage(0);
    setLoading(true);
    loadOrdersWithUsers(params.join("&"))
      .then(handlePageLoaded)
      .catch((err) => error(parseAxiosError(err)))
      .finally(() => setLoading(false));
  }, [search, statues]);

  useEffect(() => onSearchAndStatus(), [onSearchAndStatus]);

  // not included in onSearchAndStatus as when search and status is changed, page shall be set to 0!
  const onPageChange = (number) => {
    console.log("Orders onPageChange", number);
    let params = ["page=" + number];
    if (searchLock && searchLock.length) {
      params.push("key=" + searchLock);
    }
    if (statuesLock && statuesLock.length) {
      params.push("status=" + statuesLock.join(","));
    }
    setPage(number);
    setLoading(true);
    loadOrdersWithUsers(params.join("&"))
      .then(handlePageLoaded)
      .catch((err) => error(parseAxiosError(err)))
      .finally(() => setLoading(false));
  };

  const onRowClick = (index) => {
    console.log("Orders onRowClick", index);
    setId(orders[index].id);
  };

  const onUpdate = (newOrder) => {
    console.log("Orders onUpdate", newOrder);
    dispatch(updateOrders());

    const newOrders = orders.map((o) => {
      if (o.id === newOrder.id) {
        newOrder.images = null; // not needed
        if (!newOrder.sellerData) {
          newOrder.sellerData = o.sellerData;
        }
        newOrder.clientData = o.clientData;
        return newOrder;
      }
      return o;
    });
    setOrders(newOrders);
  };

  const isSelected = (item) => {
    return item.id === id;
  };

  const onStatusesChange = (newValue) => {
    setStatuses(optionsToValues(newValue));
  };

  const renderClient = (order) => {
    const client = order.clientData;
    if (client) {
      return client.name;
    }
    return formatPhoneString(order.client);
  };

  const renderSeller = (order) => {
    if (!order.seller) {
      return null;
    }
    const seller = order.sellerData;
    if (seller) {
      return seller.name;
    }
    return formatPhoneString(order.seller);
  };

  const renderStatus = (status, text) => {
    let color = "success";
    if (status === "NEW") color = "secondary";
    if (status === "PROCESSING") color = "primary";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span className={`badge bg-${color} px-2 py-1 fs-7`}>{text}</span>
      </div>
    );
  };

  const renderChatStatus = (order) => {
    const isComment = order?.comments.length > 0;
    const isMessage = order?.messages.length > 0;
    const isNewComment =
      order?.id === id
        ? false
        : order?.comments?.some(
            (comment) => !comment.seenBy?.includes(user.phone)
          ) || false;
    const isNewMessage =
      order?.id === id
        ? false
        : order?.messages?.some(
            (message) => !message.seenBy?.includes(user.phone)
          ) || false;

    const pulseAnimation = {
      animation: "pulse 0.7s infinite",
      color: "#ff4444",
    };

    const normalStyle = {
      color: "#666666",
    };

    return (
      <>
        <style>
          {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.4; }
            100% { opacity: 1; }
          }
        `}
        </style>
        {isComment ? (
          <FaRegCommentAlt
            style={isNewComment ? pulseAnimation : normalStyle}
          />
        ) : (
          <FaRegCommentAlt style={{ color: "transparent" }} />
        )}
        {isMessage && (
          <FaRegComments
            style={{
              marginLeft: "4px",
              ...(isNewMessage ? pulseAnimation : normalStyle),
            }}
          />
        )}
      </>
    );
  };

  const tableLayout = [
    { name: "Nr", key: (o) => inlineReturn(o.number), width: "5%" },
    {
      name: " ",
      key: (o) => renderStatus(o.status, parseOrderStatus(o.status)),
      width: "5%",
    },
    { name: "Klient", key: (o) => renderClient(o), width: "30%" },
    { name: "Pracownik", key: (o) => renderSeller(o), width: "30%" },
    {
      name: "Utworzone",
      key: (o) => datetimeStringToStringAgo(o.createdAt),
      width: "20%",
    },
    {
      name: " ",
      key: (o) => renderChatStatus(o),
      width: "15%",
    },
  ];

  return (
    <Col sm={{ size: 10, offset: 1 }} md={{ size: 10, offset: 1 }}>
      <Row>
        <Col sm={6}>
          <Row className="mt-3">
            <Col>
              <Input
                type="text"
                placeholder={"Fragment nr tel. albo imienia i nazwiska klienta"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Select
              isMulti
              options={statusOptions()}
              onChange={onStatusesChange}
              placeholder={"Status zamówienia"}
            />
          </Row>
          <Row className="mt-3">
            <CustomTable
              loading={loading}
              data={orders}
              layout={tableLayout}
              onRowClick={onRowClick}
              isSelected={isSelected}
            />
            <CustomPagination
              visible={!loading && orders.length}
              current={page}
              total={totalPages}
              onChange={onPageChange}
            />
          </Row>
        </Col>
        <Col sm={6}>
          <CustomOrderDetails
            id={id}
            refreshedAt={refreshedAt}
            onUpdate={onUpdate}
          />
        </Col>
      </Row>
    </Col>
  );
}
